// React
import { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Components
import { Crumbs, PageHead } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetCommunityStructureQuery } from "state/communities/communitiesApiSlice";

// Utils
import { generateOrganization } from "pages/Structure/Node/utils";

const NODE_VISUALS = {
  COMMUNITY: { icon: "apartment", tint: "rec-tint-teal" },
  METER_CPE: { icon: "electric_meter", tint: "rec-tint-info" },
  METER_INTERNAL: { icon: "electric_meter", tint: "rec-tint-amber" },
  RESOURCE: { icon: "energy_savings_leaf", tint: "rec-tint-green" },
};

function TreeCard({ icon, tint, title, sub, onClick }) {
  const isClickable = Boolean(onClick);
  const content = (
    <>
      <div className={`rec-tree-ic ${tint}`}>
        <Icon name={icon} fill />
      </div>
      <div className="tx">
        <b>{title}</b>
        {sub && <div className="rec-tree-card-sub">{sub}</div>}
      </div>
    </>
  );

  if (!isClickable) {
    return <div className="rec-tree-card">{content}</div>;
  }

  return (
    <div
      className="rec-tree-card rec-tree-card-link"
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      {content}
    </div>
  );
}

TreeCard.propTypes = {
  icon: PropTypes.string.isRequired,
  tint: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  sub: PropTypes.node,
  onClick: PropTypes.func,
};

TreeCard.defaultProps = {
  sub: null,
  onClick: null,
};

function nodeSub(org, t, onMemberClick) {
  if (org.type === "COMMUNITY") return null;
  if (org.type === "METER_CPE") {
    return (
      <span className="rec-tree-meta-text">
        <strong>{t("structure.responsible_member")}:</strong>
        {org.responsibleMemberId ? (
          <span
            className="rec-tree-member-link"
            role="link"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              onMemberClick(org.responsibleMemberId);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onMemberClick(org.responsibleMemberId);
              }
            }}
          >
            {org.responsibleMember}
          </span>
        ) : (
          <span>{t("structure.no_contract")}</span>
        )}
      </span>
    );
  }
  if (org.type === "METER_INTERNAL") return null;
  if (org.type === "RESOURCE") {
    const owners = org.owners ?? [];
    if (owners.length === 0) return t("structure.no_members");

    return (
      <span className="rec-tree-owner-list">
        {owners.map((owner) => (
          <span
            key={`${owner.owner_id}-${owner.owner_perc}`}
            className="rec-tree-owner rec-tree-member-link"
            role="link"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              onMemberClick(owner.owner_id);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onMemberClick(owner.owner_id);
              }
            }}
          >
            <strong>{owner.owner_name}</strong>
            <span>{`${owner.owner_perc}%`}</span>
          </span>
        ))}
      </span>
    );
  }
  return null;
}

function TreeNode({ org, t, onNodeClick, onMemberClick }) {
  const visual = NODE_VISUALS[org.type] || NODE_VISUALS.RESOURCE;
  const children = org.organizationChildRelationship ?? [];
  const isNavigable =
    (org.type === "METER_CPE" && org.contractId) || (org.type === "RESOURCE" && org.id);

  return (
    <>
      <TreeCard
        icon={visual.icon}
        tint={visual.tint}
        title={org.tradingName}
        sub={nodeSub(org, t, onMemberClick)}
        onClick={isNavigable ? () => onNodeClick(org) : null}
      />
      {children.length > 0 && (
        <ul>
          {children.map((child, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${child.type}-${child.tradingName}-${i}`}>
              <TreeNode org={child} t={t} onNodeClick={onNodeClick} onMemberClick={onMemberClick} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

TreeNode.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  org: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onNodeClick: PropTypes.func.isRequired,
  onMemberClick: PropTypes.func.isRequired,
};

const LEGEND = [
  { icon: "apartment", tint: "rec-tint-teal", key: "community" },
  { icon: "electric_meter", tint: "rec-tint-info", key: "cpe" },
  { icon: "electric_meter", tint: "rec-tint-amber", key: "internal" },
  { icon: "energy_savings_leaf", tint: "rec-tint-green", key: "resource" },
];

function Structure() {
  const [zoom, setZoom] = useState(1);
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["community"]);
  const navigate = useNavigate();

  const containerRef = useRef(null);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    const el = containerRef.current;
    const start = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    el.style.cursor = "grabbing";

    const onMove = (ev) => {
      el.scrollLeft = start.sl - (ev.clientX - start.x);
      el.scrollTop = start.st - (ev.clientY - start.y);
    };
    const onUp = () => {
      el.style.cursor = "grab";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  const { data: structureData } = useGetCommunityStructureQuery(community.id);
  const org = generateOrganization(structureData, community);
  const onNodeClick = useCallback(
    (node) => {
      if (node.type === "RESOURCE") {
        navigate(`/communities/${community.id}/infrastructure/resources/${node.id}`);
      }
      if (node.type === "METER_CPE") {
        navigate(`/communities/${community.id}/infrastructure/contracts/${node.contractId}`, {
          state: {
            meterId: node.id,
            meterIdentifier: node.tradingName,
            action: "view",
          },
        });
      }
    },
    [community.id, navigate]
  );
  const onMemberClick = useCallback(
    (memberId) => navigate(`/communities/${community.id}/members/${memberId}`),
    [community.id, navigate]
  );

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="community-structure" />
      <Crumbs items={[{ label: community?.name || "—" }, { label: t("structure.title") }]} />
      <PageHead
        title={t("structure.title")}
        subtitle={t("structure.subtitle")}
        actions={
          <div className="rec-seg">
            <button
              type="button"
              className="rec-seg-btn"
              title="-"
              onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
            >
              <Icon name="remove" style={{ fontSize: 16 }} />
            </button>
            <button type="button" className="rec-seg-btn" onClick={() => setZoom(1)}>
              {`${Math.round(zoom * 100)}%`}
            </button>
            <button
              type="button"
              className="rec-seg-btn"
              title="+"
              onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))}
            >
              <Icon name="add" style={{ fontSize: 16 }} />
            </button>
          </div>
        }
      />

      <div className="rec-card overflow-hidden">
        <div
          ref={containerRef}
          onMouseDown={onMouseDown}
          role="presentation"
          className="overflow-auto"
          style={{
            cursor: "grab",
            padding: "28px 20px",
            minHeight: 480,
            background: "var(--surface-2)",
          }}
        >
          <div
            className="w-fit mx-auto"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "top center",
              transition: "transform 0.2s",
            }}
          >
            <div className="rec-orgtree">
              <TreeNode org={org} t={t} onNodeClick={onNodeClick} onMemberClick={onMemberClick} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-[18px] mt-[14px] px-1">
        {LEGEND.map((l) => (
          <div key={l.key} className="flex items-center gap-[7px] text-[12.5px] text-ink-2">
            <div className={`rec-tree-ic ${l.tint}`} style={{ width: 24, height: 24 }}>
              <Icon name={l.icon} fill style={{ fontSize: 15 }} />
            </div>
            {t(`structure.legend.${l.key}`)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Structure;
